import {
  Badge,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EditIcon, TagsIcon } from "lucide-react";
import { formatDate } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import type { ExpenseCategoryDTO } from "../api/client";

type ExpenseCategoriesTableProps = {
  data: ExpenseCategoryDTO[];
  isLoading: boolean;
  canManage: boolean;
  onEdit: (row: ExpenseCategoryDTO) => void;
  emptyMessage?: string;
};

export function ExpenseCategoriesTable({
  data,
  isLoading,
  canManage,
  onEdit,
  emptyMessage = "ຍັງບໍ່ມີຫມວດ",
}: ExpenseCategoriesTableProps) {
  const columns: TanstackReactTable.ColumnDef<ExpenseCategoryDTO>[] = [
    {
      id: "sortOrder",
      header: "ລຳດັບ",
      size: 72,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm tabular-nums">
          {row.original.sortOrder}
        </span>
      ),
    },
    {
      id: "code",
      header: "ລະຫັດ",
      size: 140,
      enableSorting: false,
      cell: ({ row }) => (
        <Badge variant="outline" className="font-mono font-normal text-xs">
          {row.original.code}
        </Badge>
      ),
    },
    {
      ...createSortableColumn<ExpenseCategoryDTO>("name", "ຊື່"),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="min-w-40">
          <p className="font-medium text-sm">{row.original.name}</p>
        </div>
      ),
    },
    {
      id: "isActive",
      header: "ສະຖານະ",
      size: 120,
      enableSorting: false,
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? "default" : "secondary"}>
          {row.original.isActive ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
        </Badge>
      ),
    },
    {
      id: "updatedAt",
      header: "ອັບເດດລ່າສຸດ",
      size: 140,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDate(row.original.updatedAt)}
        </span>
      ),
    },
    ...(canManage
      ? [
          {
            id: "actions",
            header: "",
            size: 80,
            enableSorting: false,
            cell: ({
              row,
            }: {
              row: { original: ExpenseCategoryDTO };
            }) => (
              <RowActions
                maxInline={1}
                actions={[
                  {
                    label: "ແກ້ໄຂ",
                    icon: <EditIcon className="size-4" />,
                    onClick: () => onEdit(row.original),
                  },
                ]}
              />
            ),
          } satisfies TanstackReactTable.ColumnDef<ExpenseCategoryDTO>,
        ]
      : []),
  ];

  if (!isLoading && data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
        <div className="flex size-12 items-center justify-center rounded-lg border border-dashed bg-muted/30">
          <TagsIcon className="size-5 text-muted-foreground/70" />
        </div>
        <div>
          <p className="font-medium text-sm">{emptyMessage}</p>
          <p className="mt-1 max-w-sm text-muted-foreground text-xs">
            {canManage
              ? "ເພີ່ມຫມວດໃໝ່ເພື່ອໃຊ້ຕອນບັນທຶກຄ່າໃຊ້ຈ່າຍ"
              : "ຍັງບໍ່ມີຫມວດທີ່ສາມາດເບິ່ງໄດ້"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <DataTable<ExpenseCategoryDTO, unknown>
      noDataMessage={emptyMessage}
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={0}
      limit={Math.max(data.length, 20)}
      totalCount={data.length}
      onPaginationChange={() => {}}
      onSortingChange={() => {}}
    />
  );
}
